import { useEffect, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  listMailSenderPage,
  tenantMailSendersQueryKey,
  type MailSenderListGroup,
} from "../../api/mail-api.js";
import { useAuthStore } from "../../auth/auth-store.js";

/**
 * Read every page needed to render an exact sender inventory.
 *
 * Operational records are action surfaces, so silently stopping at 50 can
 * strand a draft or turned-off server with no way to edit, test, enable, or
 * archive it. Delivery history also needs the complete retained inventory to
 * resolve the server name on older rows after its server filter was removed.
 *
 * Its own module rather than a local of CommunicationAdmin (PB-5435): the
 * credential dialogs on Members & roles answer "is email going out?" from the
 * same operational inventory, and importing it from the page would pull the
 * whole Email & notifications page into theirs. One definition also keeps one
 * query key and one page shape, which is what lets the two surfaces share a
 * single cache entry instead of reading the inventory twice.
 */
export function useAllMailSenderPages(
  group: Exclude<MailSenderListGroup, "default">,
  scope = "admin",
  enabled = true,
) {
  /* Read here rather than passed in, so no caller can build the key without
     the tenant — see `tenantMailSendersQueryKey`. */
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  const query = useInfiniteQuery({
    queryKey: [...tenantMailSendersQueryKey(tenantId), scope, group],
    queryFn: ({ pageParam }) =>
      listMailSenderPage({ group, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    enabled,
  });

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = query;
  useEffect(() => {
    if (isFetchNextPageError) return;
    /* `cancelRefetch: false` because this inventory can have more than one
       observer mounted at once — Members & roles reads it from the page and
       from the invite dialog. Both effects run in the same commit, and the
       default would let the second call cancel the first's in-flight page and
       request it again. */
    if (hasNextPage && !isFetchingNextPage)
      void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError]);

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.senders) ?? [],
    [query.data],
  );
  return { ...query, rows };
}
